import type { InjectionToken } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, map, NEVER, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, skip, switchMap, tap } from 'rxjs/operators';

import { APP_CONFIG, backoffRetry, filterBoolean, filterDefined } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import type { TellentAdminAppConfig, TellentMember } from '@core/tellent-types';
import { BRAND, ORG_GUID, SeatKind } from '@core/tellent-types';
import { TellentComponentsNotificationService } from '@core/tellent-util';
import { WindowRef } from '@core/user-interface-legacy';
import { deepEqual } from '@core/utils-legacy';

import { TellentCurrentUserHttpClient } from './tellent-current-user.http';
import {
  findSeatByKind,
  TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
} from './tellent-current-user.store';
import { TellentFeatureDiscoveryActions } from '../../feature-discovery/feature-discovery.store';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserEffects {
  public effects = createEffectFactory(TellentCurrentUserEntityActions, this.actions$);
  public store = inject(Store);
  public brand = inject(BRAND);
  public windowRef = inject(WindowRef);
  public appConfig = inject(<InjectionToken<TellentAdminAppConfig>>APP_CONFIG);
  public notificationsService = inject(TellentComponentsNotificationService);
  public i18n = inject(I18nService);
  public orgGuid = inject(ORG_GUID);
  public router = inject(Router);

  public fetchCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.fetch),
      switchMap(() =>
        this.tellentCurrentUserHttpClient.fetchCurrentUser().pipe(
          map(response =>
            TellentCurrentUserEntityActions.fetchSuccess({
              payload: { ...response, currentOrgGuid: this.orgGuid! },
            }),
          ),
          catchError(error => of(TellentCurrentUserEntityActions.fetchFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updatePassword),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updatePassword(payload).pipe(
          map(response =>
            TellentCurrentUserEntityActions.updatePasswordSuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.updatePasswordFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public updateCurrentUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updateCurrentUserData),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updateCurrentUserData(payload).pipe(
          tap(() =>
            this.notificationsService.success(
              this.i18n.translate('tlc.current_user.dialog_2fa_sms.user_profile_form.success'),
            ),
          ),
          map(response =>
            TellentCurrentUserEntityActions.updateCurrentUserDataSuccess({
              payload: response,
            }),
          ),
          catchError(errorResponse => {
            const errorMessage = errorResponse?.error.errors?.avatar;
            const messageString = errorMessage && errorMessage[0];
            if (messageString) this.notificationsService.error(messageString);
            return of(
              TellentCurrentUserEntityActions.updateCurrentUserDataFail({ payload: errorResponse }),
            );
          }),
        ),
      ),
    ),
  );

  public reloadAfterLanguageChange$ = createEffect(
    () =>
      this.store.select(TellentCurrentUserSelectors.data).pipe(
        filterBoolean,
        map(({ language }) => language),
        distinctUntilChanged(),
        skip(1),
        tap(() =>
          // We need to clear the outlet before reloading the app, otherwise the dialog will be opened on refresh.
          this.router
            .navigate([{ outlets: { dialog: null } }])
            .then(() => this.windowRef.nativeWindow.location.reload()),
        ),
      ),
    { dispatch: false },
  );

  public reloadAfterAppLanguageChange$ = createEffect(
    () =>
      this.store.select(TellentCurrentUserSelectors.data).pipe(
        filterBoolean,
        map(({ appLanguages, language }) => {
          if (this.brand === 'recruitee') return appLanguages?.recruitment;
          if (this.brand === 'kiwi') return appLanguages?.coreHr;
          return language;
        }),
        distinctUntilChanged(),
        skip(1),
        tap(data =>
          this.router
            .navigate([{ outlets: { dialog: null } }])
            .then(() => this.windowRef.nativeWindow.location.reload()),
        ),
      ),
    { dispatch: false },
  );

  public leaveCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.leaveCompany),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.leaveCompany(payload.orgGuid).pipe(
          map(response =>
            TellentCurrentUserEntityActions.leaveCompanySuccess({ payload: response }),
          ),
          tap(() => this.windowRef.nativeWindow.location.replace(this.appConfig.tellentAuthUrl)),
          catchError(error =>
            of(TellentCurrentUserEntityActions.leaveCompanyFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.deleteAccount),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.deleteAccount(payload.code).pipe(
          map(response =>
            TellentCurrentUserEntityActions.deleteAccountSuccess({ payload: response }),
          ),
          tap(() => this.windowRef.nativeWindow.location.replace(this.appConfig.tellentAuthUrl)),
          catchError(error => {
            const message = error.error.errors[0];
            if (message) this.notificationsService.error(message);
            return of(TellentCurrentUserEntityActions.deleteAccountFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public pollUserIfRecruitmentAppEnabledButNotSyncedYet$ = createEffect(
    () => {
      const isRecruitmentSeatMissingAppData = (member: TellentMember) => {
        const recruitmentSeat = findSeatByKind(SeatKind.Recruitment, member);
        return recruitmentSeat !== undefined && deepEqual(recruitmentSeat.app.data, {});
      };

      return this.store.pipe(
        select(TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe),
        filterDefined,
        distinctUntilChanged(deepEqual),
        map(isRecruitmentSeatMissingAppData),
        tap(() => this.store.dispatch(TellentCurrentUserEntityActions.fetch())),
        switchMap(missing =>
          missing
            ? throwError(() => new Error(`Recruitment seat doesn't contain app data`))
            : EMPTY,
        ),
        backoffRetry({ count: 3, delay: 1000 }),
        catchError(() => NEVER),
      );
    },
    { dispatch: false, useEffectsErrorHandler: false },
  );

  public updateFeatureDiscovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentFeatureDiscoveryActions.updateSuccess),
      map(({ payload }) => TellentCurrentUserEntityActions.updateFeatureDiscovery({ payload })),
    ),
  );

  constructor(
    private tellentCurrentUserHttpClient: TellentCurrentUserHttpClient,
    private actions$: Actions,
  ) {}
}
